<script setup lang="ts">
import { defineAsyncComponent, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppSidebar from './AppSidebar.vue'
import AppTopBar from './AppTopBar.vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import SessionExpiryDialog from '@/components/common/SessionExpiryDialog.vue'
import AccountExpiredDialog from '@/components/layout/AccountExpiredDialog.vue'
import AddCreditsDialogHost from '@/features/usercredits/components/AddCreditsDialogHost.vue'
import SubscriptionAlertBanner from '@/components/layout/SubscriptionAlertBanner.vue'
import ImpersonationBanner from '@/components/layout/ImpersonationBanner.vue'
import { useSessionManager } from '@/composables/useSessionManager'
import { useOnlineStatus } from '@/composables/useOnlineStatus'
import { useAppBootstrap } from '@/composables/useAppBootstrap'
import { useBulkDownloadCreditSync } from '@/composables/useBulkDownloadCreditSync'
import { useAuth } from '@/composables/useAuth'
import { useThemeSwitch } from '@/composables/useThemeSwitch'
import { getRuntimeConfig } from '@/config/runtime-config'
// DEV-7125 — aiassistant imports are BY FILE PATH, never the feature barrel: the
// barrel statically exports AiAssistantView, which would fold the lazily-routed
// page (and the markdown/highlight.js chain) into this eager layout chunk.
import AiAssistantFab from '@/features/aiassistant/components/AiAssistantFab.vue'
import { useAiAvailability } from '@/features/aiassistant/composables/useAiPanel'
import { useAiPanelStore } from '@/features/aiassistant/stores/ai-panel.store'
import { useTour } from '@/features/tour' // layout→feature barrel precedent: AppTopBar

const route = useRoute()
const aiPanelStore = useAiPanelStore()
const AiAssistantDrawer = defineAsyncComponent({
	loader: () => import('@/features/aiassistant/components/AiAssistantDrawer.vue'),
	// Chunk failure with persisted isOpen=true would strand the assistant: the
	// drawer never renders while the FAB stays hidden (it hides while isOpen).
	// Retry up to twice; on final failure close the panel so the FAB reappears.
	// NOTE: after fail(), the loader re-runs only when the async wrapper REMOUNTS
	// (aiAvailable false→true via a hideAiFab route round-trip, or a reload) — a
	// FAB click alone does NOT re-import.
	onError: (_err, retry, fail, attempts) => {
		if (attempts <= 2) {
			retry()
			return
		}
		aiPanelStore.close()
		fail()
	},
})
const { isAvailable: aiAvailable } = useAiAvailability()
// useTour() spreads the tour engine — isRunning included; useTourEngine itself
// is NOT exported from the tour barrel.
const { explorePanelOpen, isRunning: tourRunning } = useTour()
const { logout } = useAuth()
// The server's limit, the same as the classic app's. main.ts loads the runtime config before
// mounting, so it is in place here.
const { isSessionExpiring, start, renewSession, reschedule } = useSessionManager(
	getRuntimeConfig().sessionTimeoutMinutes,
)
const { isOnline } = useOnlineStatus()

useAppBootstrap()
useThemeSwitch()
// Keep the header credit balance in sync when a bulk download finishes anywhere in the
// session — no full page reload needed (DEV-7125).
useBulkDownloadCreditSync()

async function handleSessionLogout() {
	await logout()
}

// Re-evaluate expiry suppression when a client-side navigation changes the URL
// (e.g. into or out of a `?q=`/`?runICP=` search deep-link).
watch(
	() => route.fullPath,
	() => reschedule(),
)

onMounted(() => {
	start()
})
</script>

<template>
	<v-app :class="{ 'ai-panel-resizing': aiPanelStore.isResizing }">
		<AppSidebar />
		<v-main>
			<AppTopBar />
			<Transition name="slide-down">
				<v-alert
					v-if="!isOnline"
					type="warning"
					variant="tonal"
					density="compact"
					rounded="0"
					icon="fa-solid fa-triangle-exclamation"
					class="mb-0"
				>
					You are offline. Some features may be unavailable until your connection is restored.
				</v-alert>
			</Transition>
			<ImpersonationBanner />
			<SubscriptionAlertBanner />
			<div class="default-layout__content">
				<ErrorBoundary>
					<router-view v-slot="{ Component, route }">
						<!-- NO mode="out-in": with out-in the incoming view only mounts AFTER the
						     outgoing view's leave transition signals completion. If that signal never
						     arrives (e.g. after a programmatic router.push, or when motion is reduced and
						     CSS transitions are neutralized), the new view never mounts and the page stays
						     permanently blank. A plain crossfade mounts the incoming view immediately, so
						     navigation can never blank; the leaving view is positioned absolutely (see
						     _transitions.scss .page-leave-active) to avoid a layout jump. -->
						<Transition name="page">
							<component
								:is="Component"
								:key="route.path"
							/>
						</Transition>
					</router-view>
				</ErrorBoundary>
			</div>
		</v-main>
		<!-- DEV-7125 — the layout-level v-if is load-bearing: it genuinely unmounts
		     the drawer (watchers + onMounted lifecycle) on hideAiFab routes and when
		     the datapoint is off, and defers the async chunk fetch until available. -->
		<AiAssistantDrawer v-if="aiAvailable" />
		<AiAssistantFab :suppressed="explorePanelOpen || tourRunning" />
		<SessionExpiryDialog
			:model-value="isSessionExpiring"
			@renew="renewSession()"
			@logout="handleSessionLogout()"
		/>
		<AccountExpiredDialog />
		<AddCreditsDialogHost />
	</v-app>
</template>

<style scoped lang="scss">
/* Use flex-grow so the content area absorbs any banner above it
   without creating an unconditional 48px overflow. */
.default-layout__content {
	flex: 1 1 auto;
	// Positioning context for the crossfade: .page-leave-active is absolutely positioned
	// (see _transitions.scss) so the leaving view overlays instead of stacking below the
	// incoming one during the transition.
	position: relative;
}
</style>
